import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

interface MarkdownRendererProps {
  content: string;
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mt-8 mb-4 pb-3 border-b border-background-200/70 first:mt-0">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mt-8 mb-3 first:mt-0">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base md:text-lg font-heading font-semibold text-accent-400 mt-6 mb-2">
      {children}
    </h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-sm md:text-base font-heading font-semibold text-foreground-900 mt-5 mb-2">
      {children}
    </h4>
  ),
  p: ({ children }) => (
    <p className="text-sm leading-relaxed text-foreground-700 my-3">
      {children}
    </p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc pl-5 my-3 space-y-1.5 marker:text-accent-400">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal pl-5 my-3 space-y-1.5 marker:text-accent-400">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-sm leading-relaxed text-foreground-700">
      {children}
    </li>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-foreground-950">{children}</strong>
  ),
  em: ({ children }) => (
    <em className="italic text-foreground-600">{children}</em>
  ),
  a: ({ href, children }) => (
    <a
      href={href}
      rel="nofollow noopener"
      className="text-primary-400 underline underline-offset-2 hover:text-primary-300 transition-colors"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-accent-500/50 pl-4 py-1 my-4 bg-accent-500/5 rounded-r-lg text-foreground-600 italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="border-background-200/70 my-6" />,
  code: ({ className, children }) => {
    const isBlock = typeof className === 'string' && className.includes('language-');
    if (isBlock) {
      return <code className="font-mono text-xs text-foreground-800">{children}</code>;
    }
    return (
      <code className="px-1.5 py-0.5 rounded bg-background-200/70 font-mono text-[0.85em] text-accent-400">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="bg-background-50 border border-background-200/70 rounded-lg p-4 overflow-x-auto my-4">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-lg border border-background-200/70">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-background-200/50">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="text-left px-3 py-2.5 font-semibold text-foreground-900 border-b border-background-200/70 whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 text-foreground-700 border-b border-background-200/40 align-top">
      {children}
    </td>
  ),
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt}
      className="rounded-lg border border-background-200/70 max-w-full h-auto my-4"
    />
  ),
};

export default function MarkdownRenderer({ content }: MarkdownRendererProps) {
  return (
    <div className="markdown-doc">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
}